import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api, type EdgeApplyResult, type EdgeRoute, type ServerEdge } from '../../api';
import { useResource } from '../../hooks';
import { plural } from '../../labels';
import { ActionMenu, Callout, Icon, Section, Table } from '../primitives';
import { ConfirmDialog, ErrorNotice, StatusBadge } from '../ui';
import { QuietEmpty } from '../project/ui';
import { HelpTip } from '../primitives/HelpTip';
import { relativeTime } from '../project/projectHealth';

/**
 * ServerEdgeSection is Server Detail's "Commerce Ops edge": the one
 * OpenResty that serves ports 80 and 443 for every Commerce Ops environment
 * on the server. Its routes are rendered from each environment's domains;
 * the agent's reading of the server shows whether the server matches.
 *
 * Two explicit actions: apply every environment's routes again (the only
 * way a removed environment's routes leave the edge), and remove the edge
 * once no environment uses it — never automatic.
 */
export function edgeState(edge: ServerEdge): string {
  if (edge.mode === 'per_environment') return 'per_environment';
  if (!edge.live) return edge.mode === 'unused' ? 'unused' : 'unknown';
  if (!edge.live.installed) return edge.mode === 'unused' ? 'unused' : 'not_installed';
  return edge.live.running ? 'running' : 'stopped';
}

/** Every list present, so an older or partial answer never breaks the page. */
function normalised(edge: ServerEdge | null): ServerEdge | null {
  if (!edge || typeof edge !== 'object' || !edge.mode) return null;
  const live = edge.live
    ? { ...edge.live, fragments: edge.live.fragments ?? [], legacy_gates: edge.live.legacy_gates ?? [], stale: edge.live.stale ?? [], missing: edge.live.missing ?? [] }
    : null;
  return { ...edge, environments: edge.environments ?? [], routes: edge.routes ?? [], unread_fragments: edge.unread_fragments ?? [], live };
}

function destination(route: EdgeRoute) {
  switch (route.destination) {
    case 'redirect':
      return (
        <>
          Redirects to <code>{route.redirect_to}</code>
        </>
      );
    case 'frontend':
      return route.component ? `Storefront ${route.component}` : 'Storefront';
    default:
      return 'Magento';
  }
}

export function edgeResultSentence(result: EdgeApplyResult): string {
  const parts: string[] = [];
  if (result.action === 'started') parts.push('The edge was started');
  else if (result.action === 'reloaded') parts.push('The edge loaded the checked routes');
  else if (result.action === 'removed') parts.push('The edge was stopped and removed; its configuration stays on the server');
  else parts.push('There was no edge on the server');
  if (result.removed_fragments.length > 0) parts.push(`removed the routes of ${plural(result.removed_fragments.length, 'environment')} no longer here`);
  if (result.took_over.length > 0) parts.push(`took ports 80 and 443 over from ${plural(result.took_over.length, 'per-environment gate')}`);
  return parts.join('; ') + '.';
}

export function ServerEdgeSection({ serverId }: { serverId: string }) {
  const [reload, setReload] = useState(0);
  const edge = useResource(() => api.getServerEdge(serverId), [serverId, reload], undefined, { key: `server:${serverId}:edge:live` });
  const [busy, setBusy] = useState<'apply' | 'remove' | null>(null);
  const [result, setResult] = useState<EdgeApplyResult | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [confirmRemove, setConfirmRemove] = useState(false);

  async function run(kind: 'apply' | 'remove') {
    setBusy(kind);
    setError(null);
    setResult(null);
    try {
      const res = kind === 'apply' ? await api.reconcileServerEdge(serverId) : await api.removeServerEdge(serverId);
      setResult(res);
      setReload((n) => n + 1);
    } catch (err) {
      setError(err as Error);
    } finally {
      setBusy(null);
      setConfirmRemove(false);
    }
  }

  const data = normalised(edge.data);
  const state = data ? edgeState(data) : 'unknown';
  const live = data?.live ?? null;
  const canApply = data?.mode === 'edge';
  const canRemove = data?.mode === 'unused' && !!live?.installed && data.unread_fragments.length === 0;

  return (
    <Section
      title={
        <>
          Commerce Ops edge
          <HelpTip label="What the edge is">
            One OpenResty answers ports 80 and 443 for every environment placed here. A deployment updates its own routes; a change that fails openresty -t keeps the configuration that was running.
          </HelpTip>
        </>
      }
      testId="server-edge"
      actions={
        canApply || canRemove ? (
          <>
            {canApply ? (
              <button type="button" className="button button--secondary button--small" onClick={() => run('apply')} disabled={busy !== null} data-testid="edge-apply">
                <Icon name="refresh" />
                {busy === 'apply' ? 'Applying routes' : 'Apply routes again'}
              </button>
            ) : null}
            {canRemove ? (
              <ActionMenu
                label="More edge actions"
                size="sm"
                items={[{ label: 'Remove edge', danger: true, onClick: () => setConfirmRemove(true), testId: 'edge-remove' }]}
              />
            ) : null}
          </>
        ) : undefined
      }
    >
      {edge.error && !data ? <ErrorNotice error={edge.error} /> : null}
      {!data && edge.loading ? <QuietEmpty>Reading the edge…</QuietEmpty> : null}
      {error ? <ErrorNotice error={error} /> : null}
      {result ? (
        <Callout tone="success" role="status" testId="edge-result">
          {edgeResultSentence(result)}
        </Callout>
      ) : null}

      {data ? (
        <>
          <p className="summary-line" data-testid="edge-summary">
            <StatusBadge kind="edge" value={state} />
            {data.mode === 'edge' ? (
              <span>
                {plural(data.environments.length, 'environment')}, {plural(data.routes.length, 'hostname')}
              </span>
            ) : null}
            {live ? <span className="muted">Read from the server {relativeTime(live.checked_at)}</span> : null}
          </p>

          {data.mode === 'per_environment' ? (
            <QuietEmpty>
              Each environment here runs its own gate, so only one of them can answer ports 80 and 443. With agent 0.3.4 or later every environment is served
              through one edge; each environment moves over on its next deployment.
            </QuietEmpty>
          ) : null}

          {data.mode === 'unused' && live?.installed && live.running ? (
            <QuietEmpty>It still runs and answers every hostname with 421 (misdirected request). Remove it when no environment will come back to this server.</QuietEmpty>
          ) : null}

          {data.mode === 'edge' && !live && data.live_error ? (
            <p className="muted" data-testid="edge-live-error">
              {data.live_error} The routes below are what Commerce Ops applies.
            </p>
          ) : null}

          {data.mode === 'edge' && state === 'stopped' ? (
            <Callout tone="danger" title="The edge is not running" testId="edge-stopped">
              Nothing answers ports 80 and 443 for the environments below. Apply routes again to start it.
            </Callout>
          ) : null}

          {live && (live.missing.length > 0 || live.stale.length > 0) ? (
            <Callout tone="warning" title="The server's routes differ from Commerce Ops" testId="edge-drift">
              {live.missing.length > 0 ? (
                <p>
                  {plural(live.missing.length, 'environment')} {live.missing.length === 1 ? 'has' : 'have'} no routes on the server yet. Apply routes again
                  writes them.
                </p>
              ) : null}
              {live.stale.length > 0 ? (
                <p>
                  The server still routes {plural(live.stale.length, 'environment')} no longer placed here ({live.stale.join(', ')}). Apply routes again removes{' '}
                  {live.stale.length === 1 ? 'it' : 'them'}.
                </p>
              ) : null}
            </Callout>
          ) : null}

          {live && live.legacy_gates.length > 0 ? (
            <Callout tone="info" title="Per-environment gates still run" testId="edge-legacy">
              {live.legacy_gates.join(', ')}
            </Callout>
          ) : null}

          {data.unread_fragments.length > 0 ? (
            <Callout tone="warning" title="Some routes could not be read" testId="edge-unread">
              The routes of {data.unread_fragments.join(', ')} could not be rendered from Commerce Ops. The server keeps the routes it has for{' '}
              {data.unread_fragments.length === 1 ? 'it' : 'them'}, and the edge cannot be removed until they can be read.
            </Callout>
          ) : null}

          {data.routes.length > 0 ? (
            <Table label="Routes of the Commerce Ops edge" className="edge-routes" testId="edge-routes">
              <thead>
                <tr>
                  <th scope="col">Hostname</th>
                  <th scope="col">Environment</th>
                  <th scope="col">Goes to</th>
                  <th scope="col">HTTPS</th>
                </tr>
              </thead>
              <tbody>
                {data.routes.map((route) => (
                  <tr key={`${route.environment_id}-${route.hostname}`}>
                    <th scope="row">
                      <code translate="no">{route.hostname}</code>
                    </th>
                    <td>
                      <Link to={`/app/projects/${route.project_id}/infrastructure`}>
                        {route.project} · {route.environment}
                      </Link>
                    </td>
                    <td>{destination(route)}</td>
                    <td>{route.tls ? 'Yes' : <span className="muted">Not yet</span>}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : null}
        </>
      ) : null}

      {confirmRemove ? (
        <ConfirmDialog
          open={true}
          title="Remove the Commerce Ops edge from this server?"
          description="The edge container is stopped and removed. Nothing answers ports 80 and 443 for Commerce Ops until an environment is deployed here again, which installs a new edge. The configuration directory stays on the server."
          confirmLabel="Remove edge"
          danger={true}
          busy={busy === 'remove'}
          onConfirm={() => run('remove')}
          onCancel={() => setConfirmRemove(false)}
        />
      ) : null}
    </Section>
  );
}

/**
 * EnvironmentEntryPoint is the project's note on how an environment is
 * reached: through the server's shared Commerce Ops edge, or not. It reads
 * the edge without asking the server (live=false).
 */
export function EnvironmentEntryPoint({ serverId, serverName, environmentId }: { serverId: string; serverName: string; environmentId: string }) {
  const edge = useResource(() => api.getServerEdge(serverId, { live: false }), [serverId], undefined, { key: `server:${serverId}:edge:cached` });
  if (edge.loading && !edge.data) return <span className="muted">Reading…</span>;
  const data = normalised(edge.data);
  const member = data?.environments.find((env) => env.environment_id === environmentId);
  if (!data || !member) return <span className="muted">Not through the shared Commerce Ops edge</span>;
  const others = data.environments.length - 1;
  return (
    <span data-testid="environment-entry-point">
      Shared Commerce Ops edge on <Link to={`/app/servers/${serverId}`}>{serverName}</Link>
      <span className="muted">
        {' '}
        — {plural(member.routes, 'hostname')}
        {others > 0 ? `, shared with ${plural(others, 'other environment')}` : ''}
      </span>
    </span>
  );
}
